import { AuthCardLogInWithAppleButton } from "@/components/auth/apple/auth-card-log-in-with-apple-button";
import { observer } from "mobx-react-lite";
import { FC, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { appRoutes } from "@/app/router/appRoutes";
import { appleIdentityModule } from "@/modules/apple-identity";
import { googleIdentityModule } from "@/modules/google-identity";
import { AuthCardLogInWithGoogleButton } from "@/components/auth/google/auth-card-log-in-with-google-button";
import { AuthCardBase } from "@/components/auth/auth-card-base";
import { usePublicAppStore } from "@/store";
import { AuthCardLogInWithEmailPasswordButton } from "@/components/auth/email-password/auth-card-log-in-with-email-password-button";
import { LastUsedAuthMethod } from "@/domains/auth/last-used-auth-method";
import { AuthCardLastUsedBadge } from "@/components/auth/auth-card-last-used-badge";
import styled from "@emotion/styled";

export interface AuthCardLogInProps {
  showEmailPasswordButton?: boolean;
}

export const AuthCardLogIn: FC<AuthCardLogInProps> = observer(
  ({ showEmailPasswordButton = false }) => {
    const navigate = useNavigate();
    const { publicStore } = usePublicAppStore();
    const lastUsedAuthMethod = publicStore.auth.lastUsedAuthMethod;

    const shouldShowEmailPasswordButton = useMemo(() => {
      if (publicStore.debug.debugModeEnabled) return true;
      if (showEmailPasswordButton) return true;
      // If the user recently logged in with email/password, we keep this visible for convenience.
      if (lastUsedAuthMethod === LastUsedAuthMethod.EmailPassword) return true;
      return false;
    }, [lastUsedAuthMethod, publicStore.debug.debugModeEnabled, showEmailPasswordButton]);

    const logInWithGoogle = useCallback(async () => await googleIdentityModule.triggerLogIn(), []);
    const logInWithApple = useCallback(async () => await appleIdentityModule.triggerLogIn(), []);

    const logInWithEmailPassword = useCallback(() => {
      navigate(appRoutes.logInEmailPassword({}).path);
    }, [navigate]);

    return (
      <AuthCardBase>
        <AuthMethodButtonWrapper>
          <AuthCardLogInWithGoogleButton onClick={logInWithGoogle} />
          {lastUsedAuthMethod === LastUsedAuthMethod.Google && <StyledLastUsedBadge />}
        </AuthMethodButtonWrapper>
        <AuthMethodButtonWrapper>
          <AuthCardLogInWithAppleButton onClick={logInWithApple} />
          {lastUsedAuthMethod === LastUsedAuthMethod.Apple && <StyledLastUsedBadge />}
        </AuthMethodButtonWrapper>
        {shouldShowEmailPasswordButton && (
          <AuthMethodButtonWrapper>
            <AuthCardLogInWithEmailPasswordButton onClick={logInWithEmailPassword} />
            {lastUsedAuthMethod === LastUsedAuthMethod.EmailPassword && <StyledLastUsedBadge />}
          </AuthMethodButtonWrapper>
        )}
      </AuthCardBase>
    );
  }
);

const AuthMethodButtonWrapper = styled.div`
  position: relative;
  width: fit-content;
`;

const StyledLastUsedBadge = styled(AuthCardLastUsedBadge)`
  position: absolute;
  right: 16px;
  top: 0;
  transform: translateY(-50%);
`;
